// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';

// ** Icons
import { Icon } from '@/components/common/icons';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { FooterSocialLink } from '@/types/layoutProps';
import type { MegaMenuContactBlock } from './megaMenuView.types';

type MegaMenuContactFooterProps = {
	contact: MegaMenuContactBlock;
	socialLinks: FooterSocialLink[];
	mobile?: boolean;
};

const CONTACT_LABEL_SX = {
	color: 'common.white',
	opacity: 0.9,
	whiteSpace: 'pre-line'
} as const;

const contactValueSx = {
	color: 'common.white',
	opacity: 0.85,
	whiteSpace: 'pre-line'
} as const;

const contactHeadingSx = {
	color: 'common.white',
	fontWeight: 500
} as const;

const SOCIAL_ICON_INTERACTIVE_SX = {
	opacity: 0.92,
	transition: 'opacity 0.2s ease, transform 0.2s ease',
	'&:hover': {
		opacity: 1,
		transform: 'scale(1.06)'
	}
} as const;

const ContactRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', columnGap: 4 }}>
		<Typography variant="label" sx={CONTACT_LABEL_SX}>
			{label}
		</Typography>
		{children}
	</Box>
);

const MegaMenuContactFooter = ({ contact, socialLinks, mobile = false }: MegaMenuContactFooterProps) => {
	const overlayBrown = useTheme().palette.loginOverlayCta.main;

	// Props
	const customIconColor = {
		glyphColor: overlayBrown
	};

	const rootSx: SxProps<Theme> = mobile
		? { display: 'flex', flexDirection: 'column', gap: 8 }
		: {
				display: 'grid',
				gridTemplateColumns: '1fr',
				gap: 8,
				alignItems: 'start',
				'@media (min-width:1120px)': {
					gridTemplateColumns: 'minmax(0, 1fr) auto'
				}
			};

	const addressContainerSx: SxProps<Theme> = mobile
		? {
				mt: 8,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
				rowGap: 8
			}
		: {
				mt: 8,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
				flexWrap: 'wrap',
				rowGap: 8,
				'@media (min-width:1120px)': {
					flexDirection: 'row',
					flexWrap: 'nowrap',
					columnGap: 24,
					rowGap: 0
				}
			};

	const teiStackSx: SxProps<Theme> = mobile
		? { flexShrink: 0 }
		: {
				flexShrink: 0,
				'@media (min-width:1120px)': { minWidth: 320 }
			};

	const socialStackSx: SxProps<Theme> = mobile ? { mt: 8 } : { mt: 8, '@media (min-width:1120px)': { mt: 12 } };

	return (
		<Box sx={rootSx}>
			<Box>
				<Stack spacing={0}>
					<Typography variant="label" sx={contactHeadingSx}>
						{contact.heading}
					</Typography>

					<Box sx={addressContainerSx}>
						<Stack spacing={1}>
							{contact.addressLines.map((line) => (
								<Typography key={line} variant="label" sx={contactValueSx}>
									{line}
								</Typography>
							))}
						</Stack>

						<Stack spacing={1} sx={teiStackSx}>
							{contact.phone ? (
								<ContactRow label={contact.phoneLabel ?? 'T'}>
									<Typography variant="label" sx={contactValueSx}>
										{contact.phone}
									</Typography>
								</ContactRow>
							) : null}

							{contact.email ? (
								<ContactRow label={contact.emailLabel ?? 'E'}>
									<Typography variant="label" sx={contactValueSx}>
										{contact.email}
									</Typography>
								</ContactRow>
							) : null}

							{contact.faqHref ? (
								<ContactRow label="I">
									<Link href={contact.faqHref} passHref>
										<Typography variant="label" sx={contactValueSx}>
											FAQs
										</Typography>
									</Link>
								</ContactRow>
							) : null}
						</Stack>
					</Box>
				</Stack>
			</Box>

			<Box>
				<Stack spacing={0}>
					<Typography variant="label" sx={contactHeadingSx}>
						Folgen Sie uns
					</Typography>
					<Stack
						direction="row"
						useFlexGap
						spacing={2}
						sx={[
							{
								alignItems: 'center',
								flexWrap: 'wrap',
								maxWidth: 280
							},
							socialStackSx
						]}
					>
						{socialLinks.map((social) => (
							<Link
								key={social.key}
								href={social.href}
								passHref
								aria-label={social.iconAlt}
								target="_blank"
								rel="noopener noreferrer"
							>
								<Icon
									name={social.key.toLowerCase()}
									size={40}
									customIconColor={customIconColor}
									sx={SOCIAL_ICON_INTERACTIVE_SX}
								/>
							</Link>
						))}
					</Stack>
				</Stack>
			</Box>
		</Box>
	);
};

export default MegaMenuContactFooter;
